import { CameraIcon } from '@phosphor-icons/react';
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import jessicaIcon from '../assets/jessica-icon.png.asset.json';

const ICON_STORAGE_KEY = 'reema-member-spotlight:icon';

export interface MemberSpotlightIconProps {
  /** Accessible name for the photo. */
  alt: string;
  /** Optional layout classes on the wrap. */
  className?: string;
}

/**
 * Member Spotlight avatar. Photo changes stay in this browser's localStorage.
 * @param props - Alt text and optional class name.
 */
export const MemberSpotlightIcon = ({
  alt,
  className,
}: MemberSpotlightIconProps) => {
  const [src, setSrc] = useState<string>(jessicaIcon.url);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(ICON_STORAGE_KEY);
      if (saved) setSrc(saved);
    } catch {
      // localStorage unavailable — fall back to bundled default.
    }
  }, []);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setSrc(dataUrl);
      try {
        localStorage.setItem(ICON_STORAGE_KEY, dataUrl);
      } catch {
        // Persisting is best-effort; ignore quota/availability errors.
      }
    };
    reader.onerror = () => {
      // File read failed; leave existing icon in place.
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`relative group ${className ?? ''}`}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover bg-white rounded-full"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/30 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--reema-coral-100)] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        aria-label="Change member photo"
        title="Change member photo"
      >
        <CameraIcon className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        aria-hidden="true"
      />
    </div>
  );
};
